import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  // Current appointment data (raw API response)
  currentAppointment: null,

  // Loading states
  appointmentLoading: false,

  // Error states
  appointmentError: null,

  // Tab states
  activeTab: "overview",

  // Overview tab data
  workNotes: "",
  signature: null,
  customerSignatureName: "",
  customerSignatureEmail: "",
  signatureTimestamp: "",
  inventoryItems: [],
  // Time tab data
  timeRecords: [],
  fieldStatus: [],

  // Equipment tab data
  equipmentUsage: [],

  // Files tab data
  uploadedFiles: [],
};

const appointmentSlice = createSlice({
  name: "appointment",
  initialState,
  reducers: {
    // Loading and data management
    setAppointmentLoading(state, action) {
      state.appointmentLoading = action.payload;
    },
    setCurrentAppointment(state, action) {
      state.currentAppointment = action.payload;
      state.appointmentLoading = false;
      state.appointmentError = null;
    },
    setAppointmentError(state, action) {
      state.appointmentError = action.payload;
      state.appointmentLoading = false;
    },

    // Tab management
    setActiveTab(state, action) {
      state.activeTab = action.payload;
    },

    // Overview tab
    setWorkNotes(state, action) {
      state.workNotes = action.payload;
      // Optionally sync notes with currentAppointment.notes if desired
      if (state.currentAppointment) {
        state.currentAppointment.notes = action.payload;
      }
    },
    setWorkNotesError(state, action) {
      state.workNotesError = action.payload;
    },
    setSignature(state, action) {
      state.signature = action.payload.signature;
      state.customerSignatureName = action.payload.customerName;
      state.customerSignatureEmail = action.payload.customerEmail;
      state.signatureTimestamp = action.payload.timestamp;
    },

    setFieldStatus(state, action) {
      state.fieldStatus = action.payload || [];
    },

    addOrUpdateFieldStatus(state, action) {
      const record = action.payload;
      if (!record) return;
      const index = state.fieldStatus.findIndex(
        (item) => item.id === record.id
      );
      if (index > -1) {
        // Update existing record
        state.fieldStatus[index] = record;
      } else {
        // Add new record
        state.fieldStatus.unshift(record);
      }
    },

    removeFieldStatus(state, action) {
      const id = action.payload;
      state.fieldStatus = state.fieldStatus.filter((item) => item.id !== id);
    },

    // Equipment usage
    addEquipmentUsage(state, action) {
      state.equipmentUsage.unshift(action.payload);
    },
    removeEquipmentUsage(state, action) {
      state.equipmentUsage = state.equipmentUsage.filter(
        (equipment) => equipment.id !== action.payload
      );
    },
    updateEquipmentUsage(state, action) {
      const index = state.equipmentUsage.findIndex(
        (equipment) => equipment.id === action.payload.id
      );
      if (index !== -1) {
        state.equipmentUsage[index] = action.payload;
      }
    },
    updateAppointmentStatus: (state, action) => {
      const { appointmentId, newStatus } = action.payload;
      if (
        state.currentAppointment &&
        state.currentAppointment.id === appointmentId
      ) {
        state.currentAppointment.appointmentStatusId = newStatus;
        state.currentAppointment.updatedDate = new Date().toISOString();
      }
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    // Files
    addUploadedFiles(state, action) {
      state.uploadedFiles = [...action.payload, ...state.uploadedFiles];
    },
    removeUploadedFile(state, action) {
      state.uploadedFiles = state.uploadedFiles.filter(
        (file) => file.id !== action.payload
      );
    },

    // Clear appointment data
    clearAppointmentData(state) {
      return { ...initialState };
    },
    setProductsRecords(state, action) {
      state.inventoryItems = action.payload;
    },
  },
});

export const {
  setAppointmentLoading,
  setCurrentAppointment,
  setAppointmentError,
  setActiveTab,
  setWorkNotes,
  setSignature,
  addTimeRecord,
  removeTimeRecord,
  updateTimeRecord,
  addEquipmentUsage,
  removeEquipmentUsage,
  updateEquipmentUsage,
  addUploadedFiles,
  removeUploadedFile,
  clearAppointmentData,
  setError,
  updateAppointmentStatus,
  setFieldStatus,
  addOrUpdateFieldStatus,
  removeFieldStatus,
  setProductsRecords,
} = appointmentSlice.actions;

export default appointmentSlice.reducer;
